import React, { useEffect, useState } from 'react';
import { Button, Navbar, Page, Popup, NavRight, Link } from 'framework7-react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import II18 from '../../../translate/II18';
import { atrustRegisterFormStructure } from './atrustRegisterFormStructure';
import { findAllFormErrors } from '../../../helpers/formUtils';
import AtrustOption from './AtrustOption';
import RegistrationForm from '../RegistrationForm';
import { useRegistrationData } from '../useRegistrationData';
import QesSuccessFooter from '../QesSuccessFooter';
import { dispatchCompletePreviewQes } from '../completePreviewQes';

const ATRUST_ACTIONS = {
  LOGIN: 'LOGIN',
  REGISTER: 'REGISTER'
};

/**
 * Preview-only A-Trust dialog:
 * - account exists (LOGIN) → immediate fake QES success
 * - register → RegistrationForm → fake QES success
 * Shares RegistrationForm / AtrustOption with the production popup.
 */
export default function AtrustRegisterPopUpPreview(props) {
  const {
    opened,
    noanimate,
    docId,
    signatureFieldId,
    currentRecipientData,
    documentIsQualifiedSigned,
    onSignFlowDone,
    onClose
  } = props;

  const dispatch = useDispatch();
  const { registrationData } = useRegistrationData(
    signatureFieldId,
    atrustRegisterFormStructure,
    currentRecipientData
  );

  const [atrustNextAction, setAtrustNextAction] = useState('');
  const [showFormErrors, setShowFormErrors] = useState(false);
  const [phase, setPhase] = useState('choice');

  useEffect(() => {
    if (!opened) {
      setAtrustNextAction('');
      setShowFormErrors(false);
      setPhase('choice');
    }
  }, [opened]);

  useEffect(() => {
    if (opened && documentIsQualifiedSigned) {
      setPhase('success');
    }
  }, [opened, documentIsQualifiedSigned]);

  const handleClose = () => {
    if (onClose) onClose();
  };

  const completeSign = () => {
    dispatchCompletePreviewQes(dispatch, docId);
  };

  const onChoiceNext = () => {
    if (atrustNextAction === ATRUST_ACTIONS.LOGIN) {
      completeSign();
      return;
    }
    if (atrustNextAction === ATRUST_ACTIONS.REGISTER) {
      setPhase('form');
    }
  };

  const onRegisterNext = () => {
    const errors = findAllFormErrors(
      [atrustRegisterFormStructure],
      registrationData
    );
    if (errors.size) {
      setShowFormErrors(true);
      return;
    }
    completeSign();
  };

  return (
    <Popup
      id="ATRUST_REGISTER_POPUP_PREVIEW"
      className="ATRUST_REGISTER_POPUP common-dialog"
      opened={Boolean(opened)}
      onPopupClosed={handleClose}
      swipeToClose={false}
      closeByBackdropClick={false}
      closeByOutsideClick={false}
      animate={!noanimate}
    >
      <Page style={{ background: '#fff' }} className="pageWithScroll">
        <Navbar title={II18.get('dialog_atrust_settings_title')}>
          <NavRight>
            <Link popupClose onClick={handleClose}>
              <II18>title_close</II18>
            </Link>
          </NavRight>
        </Navbar>

        {phase === 'success' ? (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              padding: '40px 24px 24px',
              height: '100%',
              boxSizing: 'border-box'
            }}
          >
            <img
              src="/id_austria.png"
              width={80}
              alt="ID Austria"
              style={{ opacity: 1 }}
            />
            <h3 style={{ marginTop: 24, marginBottom: 8 }}>
              <II18>esignature_document_signed</II18>
            </h3>
            <QesSuccessFooter
              onDone={() => {
                if (onSignFlowDone) onSignFlowDone();
                else handleClose();
              }}
            />
          </div>
        ) : phase === 'form' ? (
          <div
            style={{
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between'
            }}
          >
            <div
              style={{
                flexGrow: 1,
                overflow: 'hidden',
                overflowY: 'auto',
                padding: 10,
                margin: 10,
                height: '100%'
              }}
              className="tinny-scrollbar"
            >
              <RegistrationForm
                signatureFieldId={signatureFieldId}
                signatureFields={atrustRegisterFormStructure}
                showFormErrors={showFormErrors}
                currentRecipientData={currentRecipientData}
              />
            </div>
            <div
              style={{
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'space-around',
                padding: 30,
                paddingTop: 10
              }}
            >
              <Button
                id="atrust_preview_register_back"
                style={{ width: '100%' }}
                onClick={() => {
                  setPhase('choice');
                  setShowFormErrors(false);
                  setAtrustNextAction('');
                }}
              >
                {II18.get('title_back')}
              </Button>
              <Button
                style={{ width: '100%' }}
                onClick={onRegisterNext}
                raised
                fill
              >
                {II18.get('title_next')}
              </Button>
            </div>
          </div>
        ) : (
          <div
            style={{
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'start',
              alignItems: 'center',
              paddingTop: 20
            }}
          >
            <img
              src="/id_austria.png"
              width={100}
              alt="ID Austria"
              style={{ opacity: 1 }}
            />
            <span style={{ color: '#999', fontSize: 14 }}>
              <II18>dialog_atrust_select_next_options</II18>
            </span>
            <AtrustOption
              id="atrust_preview_login_exists"
              title={II18.get('dialog_atrust_login_exists')}
              desc={II18.get('dialog_atrust_login_exists_help')}
              onClick={() => setAtrustNextAction(ATRUST_ACTIONS.LOGIN)}
              icon="material:input"
              selected={atrustNextAction === ATRUST_ACTIONS.LOGIN}
            />
            <AtrustOption
              id="atrust_preview_register"
              title={II18.get('dialog_atrust_register')}
              desc={II18.get('dialog_atrust_register_help')}
              onClick={() => setAtrustNextAction(ATRUST_ACTIONS.REGISTER)}
              icon="f7:person_crop_square"
              selected={atrustNextAction === ATRUST_ACTIONS.REGISTER}
            />
            <Button
              id="atrust_preview_next"
              style={{ marginTop: 30, width: 150 }}
              raised
              disabled={!atrustNextAction}
              fill
              onClick={onChoiceNext}
            >
              <II18>title_next</II18>
            </Button>
          </div>
        )}
      </Page>
    </Popup>
  );
}

AtrustRegisterPopUpPreview.propTypes = {
  noanimate: PropTypes.bool,
  opened: PropTypes.any,
  docId: PropTypes.string,
  onClose: PropTypes.func,
  signatureFieldId: PropTypes.string,
  currentRecipientData: PropTypes.object,
  documentIsQualifiedSigned: PropTypes.bool,
  onSignFlowDone: PropTypes.func
};
